import type { GallerySlide } from "./ClassGallery";

/**
 * Class galleries come from the backend and may still be absent on classes
 * that have not been photographed, so every carousel survives an empty list.
 *
 * `toSlides` accepts whatever shape the gallery arrives in once it is added to
 * the API — a list of URLs, or a list of objects keyed `image`/`url`/`src`
 * with an optional caption — and otherwise lays out one empty slide per
 * caption in `fallbackCaptions`. Those captions name the shot that belongs in
 * each slot, so the carousel ships with its real slider behaviour — swipe,
 * arrow, dots — and the uploads drop straight into place.
 */

type RawSlide =
  | string
  | {
      image?: string;
      url?: string;
      src?: string;
      caption?: string | { en?: string; ar?: string };
      title?: string;
      name?: string;
      fit?: "cover" | "contain";
    };

/* Gallery files are served `immutable`, and some were re-uploaded under the
   same name, so browsers keep the old picture for a year. Bump this whenever
   a gallery image is replaced in place; the query string is a new cache key
   for both the browser and CloudFront. */
const GALLERY_CACHE_VERSION = "3";

const withCacheVersion = (url?: string) =>
  url && /^https?:/.test(url)
    ? `${url}${url.includes("?") ? "&" : "?"}v=${GALLERY_CACHE_VERSION}`
    : url;

const pickImage = (raw: RawSlide) =>
  withCacheVersion(
    typeof raw === "string" ? raw : raw.image || raw.url || raw.src || undefined
  );

const pickCaption = (raw: RawSlide, language: string) => {
  if (typeof raw === "string") return undefined;
  if (typeof raw.caption === "string") return raw.caption;
  if (raw.caption) {
    const localized = language.toLowerCase().startsWith("ar")
      ? raw.caption.ar || raw.caption.en
      : raw.caption.en || raw.caption.ar;
    if (localized) return localized;
  }
  return raw.title || raw.name;
};

export function toSlides(
  raw: unknown,
  fallbackCaptions: string[],
  keyPrefix = "slide",
  language = "en"
): GallerySlide[] {
  const list = Array.isArray(raw) ? (raw as RawSlide[]) : [];

  const slides = list
    .map((item, index) => ({
      id: `${keyPrefix}-${index}`,
      image: pickImage(item),
      caption: pickCaption(item, language) ?? fallbackCaptions[index],
      fit: typeof item === "string" ? undefined : item.fit,
    }))
    .filter((slide) => slide.image || slide.caption);

  if (slides.length) return slides;

  return fallbackCaptions.map((caption, index) => ({
    id: `${keyPrefix}-placeholder-${index}`,
    caption,
  }));
}
